import { getEnv } from '@/composables/useEnv'
import {
  ISanityDataLayer,
  transformDataLayer,
  transformSanityLayerPacks
} from '@/services/adapters/transformations/transformDataLayers'
import { transformSanityEffect } from '@/services/adapters/transformations/transformSanityEffect'
import { transformSanityRemedy } from '@/services/adapters/transformations/transformSanityRemedy'
import { transformSanityTutorialCopy } from '@/services/adapters/transformations/transformSanityTutorialCopy'
import { IDataStore } from '@/services/dataStore/IDataStore'
import {
  commentCategoriesQuery,
  commentCategoryIconsQuery,
  costsQuery,
  dataLayerReferenceQuery,
  dataLayersQuery,
  effectReferenceQuery,
  effectsQuery,
  layerPacksQuery,
  remediesQuery,
  remedyCategoriesQuery,
  remedyReferenceQuery,
  tutorialCopyQuery
} from '@/services/dataStore/queries/index'
import {
  IBaseDataLayer,
  IBaseDataLayerReference,
  IBaseEffect,
  IBaseEffectReference,
  IBaseRemedy,
  IBaseRemedyReference,
  ICommentCategory,
  ICommentCategoryIcon,
  ICost,
  IRemedyCategory
} from '@lifk/types'
import { createClient, SanityClient } from '@sanity/client'

export type TPerspective = 'published' | 'previewDrafts'

const SOCKET_SILENCE_TIMEOUT_MS = 20000

export class SanityDataStore implements IDataStore {
  client: SanityClient
  disableValidations: boolean

  constructor(perspective: TPerspective, disableValidations: boolean) {
    const env = getEnv('VITE_APP_ENV')
    this.disableValidations = disableValidations

    this.client = createClient({
      projectId: getEnv('VITE_APP_SANITY_ID'),
      dataset: getEnv('VITE_APP_SANITY_DATASET'), // 'production' or 'staging'
      token: getEnv('VITE_APP_SANITY_TOKEN'),
      useCdn: env === 'production' || env === 'test',
      // use current date (YYYY-MM-DD) to target the latest API version at that date.
      // Recommended to make apiVersion static so unintended changes aren't introduced suddenly if Sanity updates.
      apiVersion: '2024-03-25',
      perspective,
      timeout: SOCKET_SILENCE_TIMEOUT_MS
    })
  }

  getRemedies = async () => {
    const resp = await this.client.fetch(remediesQuery())
    if (resp?.length) {
      const transformedRemedies: IBaseRemedy[] = resp.map((r) => {
        return transformSanityRemedy(r, this.disableValidations)
      })
      return transformedRemedies
    }
    return []
  }

  getRemedy = async (remedyKey: string) => {
    const resp = await this.client.fetch(remedyReferenceQuery(remedyKey))
    if (resp?.length) {
      const transformedRemedy = transformSanityRemedy(
        resp[0],
        this.disableValidations
      ) as IBaseRemedyReference
      return transformedRemedy
    }
    throw new Error('Remedy not found')
  }

  getRemedyCategories = async () => {
    const resp = await this.client.fetch(remedyCategoriesQuery())
    if (resp?.length) {
      return resp as IRemedyCategory[]
    }
    return []
  }

  getCommentCategories = async () => {
    const resp = await this.client.fetch(commentCategoriesQuery())
    if (resp?.length) {
      return resp as ICommentCategory[]
    }
    return []
  }

  getCommentCategoryIcons = async () => {
    const resp = await this.client.fetch(commentCategoryIconsQuery())
    if (resp?.length) {
      return resp as ICommentCategoryIcon[]
    }
    return []
  }

  getEffects = async () => {
    const resp = await this.client.fetch(effectsQuery())
    if (resp?.length) {
      const transformedEffects: IBaseEffect[] = resp.map((e) =>
        transformSanityEffect(e, this.disableValidations)
      )
      return transformedEffects
    }
    return []
  }

  getEffect = async (effectKey: string) => {
    const resp = await this.client.fetch(effectReferenceQuery(effectKey))
    if (resp?.length) {
      const rawEffect = resp[0]
      const transformedEffect: IBaseEffectReference = transformSanityEffect(
        rawEffect,
        this.disableValidations
      )
      return transformedEffect
    }
    throw new Error('Effect not found')
  }

  getCosts = async () => {
    const costsResp = await this.client.fetch(costsQuery())
    return costsResp as ICost[]
  }

  getDataLayers = async (): Promise<IBaseDataLayer[]> => {
    const resp = await this.client.fetch(dataLayersQuery())
    if (resp?.length) {
      const transformedDataLayers: (IBaseDataLayer | undefined)[] = resp.map(
        (dataLayer: ISanityDataLayer) =>
          transformDataLayer(dataLayer, false, this.disableValidations)
      )
      return transformedDataLayers.filter((dl) => !!dl)
    }
    return []
  }

  getDataLayer = async (dataLayerKey: string) => {
    const resp = await this.client.fetch(dataLayerReferenceQuery(dataLayerKey))
    if (resp?.length) {
      const transformed = transformDataLayer(
        resp[0],
        true,
        this.disableValidations
      )
      if (transformed) {
        return transformed as IBaseDataLayerReference
      }
    }
    return undefined
  }

  getLayerPacks = async () => {
    const resp = await this.client.fetch(layerPacksQuery())
    if (resp?.length) {
      return transformSanityLayerPacks(resp)
    }
    return []
  }

  getTutorialCopy = async () => {
    const resp = await this.client.fetch(tutorialCopyQuery())
    if (resp) {
      return transformSanityTutorialCopy(resp)
    }
    return undefined
  }
}
